import React, { useState } from "react";
import { Tabs } from "antd";
import { PageHeader } from "../components/PageHeader";
import { Banner } from "../components/Banner";
import { StackedAreaChart } from "../components/StackedAreaChart";
import { useTailSpend } from "../TailSpendContext";
import classes from "./ReportingView.module.less";

type TabKey = "value" | "outcome";

const TABS = [
    { key: "value" as TabKey, label: "Value captured by month" },
    { key: "outcome" as TabKey, label: "How deals closed" },
];

export const ReportingView: React.FC = () => {
    const { workspace } = useTailSpend();
    const { report } = workspace;
    const [tab, setTab] = useState<TabKey>("value");

    return (
        <>
            <PageHeader
                title="Reporting"
                subtitle="Portfolio view of what the agents delivered: savings, cycle time, coverage, supplier satisfaction."
            />

            <Banner
                headline={
                    <>
                        Portfolio across{" "}
                        <span style={{ color: "#5ad81d", fontWeight: 700 }}>
                            {workspace.suppliersInScope.toLocaleString()}
                        </span>{" "}
                        suppliers · agents have delivered{" "}
                        <span style={{ color: "#5ad81d", fontWeight: 700 }}>$2.4M</span> this year at an
                        average cycle of 5.0 business days.
                    </>
                }
                kpis={report.kpis}
            />

            <div className={classes.chartCard} data-tour-id="reportingPage">
                <Tabs
                    activeKey={tab}
                    onChange={(k) => setTab(k as TabKey)}
                    items={TABS.map((t) => ({ key: t.key, label: t.label }))}
                />
                {tab === "value" && (
                    <>
                        <div className={classes.chartSub}>
                            Realized first-year savings · by category ($K)
                        </div>
                        <StackedAreaChart
                            months={report.months}
                            series={report.valueByCategory}
                            yTickFormatter={(v) => `$${v}K`}
                        />
                    </>
                )}
                {tab === "outcome" && (
                    <>
                        <div className={classes.chartSub}>
                            Closure mix per month · share of deals
                        </div>
                        <StackedAreaChart
                            months={report.months}
                            series={report.outcomeRateByMonth}
                            yMax={100}
                            yTickFormatter={(v) => `${v}%`}
                        />
                    </>
                )}
            </div>

            {tab === "value" && (
                <div className={classes.note}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#999" }}>
                        How savings are measured
                    </div>
                    <p>
                        Savings are measured against the benchmarked market baseline at the moment of negotiation, not the
                        prior price. Working-capital gains from extended terms are reported separately as NPV. Every figure
                        traces to the deal's audit record.
                    </p>
                </div>
            )}
        </>
    );
};
